import React, { Component, PureComponent } from "react";
import _ from "lodash";
import {
  Drawer,
  withStyles,
  WithStyles,
  createStyles,
  Stepper,
  Step,
  StepLabel,
  StepContent,
  Typography,
  Grid,
  Avatar,
  IconButton,
  Button,
} from "@material-ui/core";
import LensIcon from "@material-ui/icons/Lens";
import classNames from "classnames";
import { CloseOutlined as CloseIcon } from "@material-ui/icons";
import {
  ContentRequest,
  Question,
  Subject,
  TypeOfAuthor,
} from "../types/ContentRequest";
import { RequestHistoryItem } from "../types/RequestHistory";
import contentRequestServices from "../services/contentRequestServices";
import { format } from "date-fns";
import {
  isLoggedInUserCenterHead,
  isLoggedInUserMultiCenterHead,
} from "../helpers/createContentRequest";
import { RequestContentType } from "../types/ContentRequestCreate";

const styles = () =>
  createStyles({
    drawerContainer: {
      width: "25vw",
    },
    avatar: {
      marginTop: 9,
      marginRight: 6,
      marginBottom: 6,
      marginLeft: 8,
      textTransform: "uppercase",
      display: "inline-flex",
      height: "25px",
      width: "25px",
      fontSize: "0.8rem",
    },
    creatorAvatar: {
      background: "#F5E8FF;",
      color: "#B574EB",
    },
    evaluatorAvatar: {
      background: "#E8FFCD",
      color: "#00D86C",
    },
    langEvaluatorAvatar: {
      background: "#FFDEB6",
      color: "#FF8C00",
    },
    moderatorAvatar: {
      background: "#C8DFF6",
      color: "#4E9DED",
    },
    publisherAvatar: {
      background: "#FFD0D0",
      color: "#DC3737",
    },
    dateText: {
      color: "#666",
      fontSize: "0.85rem",
      marginLeft: "3rem",
    },
    content: {
      fontSize: "1rem",
    },
    drawerHeader: {
      margin: "1rem 2rem",
    },
    drawerHeading: {
      fontSize: "1.5rem",
      fontWeight: 600,
    },
    closeIcon: {
      position: "absolute",
      top: "10px",
      right: 0,
      fontSize: "1.8rem",
    },
    stepIcon: {
      fontSize: "10px",
    },
    groupedDate: {
      fontWeight: 600,
      fontSize: "1.1rem",
    },
    spacing: {
      margin: "0 2rem",
    },
    stepLabelRoot: {
      marginLeft: "8px",
    },
    noHistoryContainer: {
      padding: "1rem",
      transform: "translateY(70%)",
      textAlign: "center",
      "& h6": {
        fontSize: "16px",
        fontWeight: 600,
        color: "#999",
        width: "70%",
        margin: "1rem auto",
      },
    },
  });

interface Props extends WithStyles<typeof styles> {
  open: boolean;
  onClose: () => void;
  contentRequest: ContentRequest;
}

interface State {
  history: RequestHistoryItem[];
  currentPage: number;
  totalNoOfPages: number;
}

const INITIAL_STATE = {
  history: [],
  currentPage: 1,
  totalNoOfPages: 0,
};

class RequestHistoryDrawer extends PureComponent<Props, State> {
  isCenterHead: boolean;
  isMultiCenterHead: boolean;
  constructor(props: Props) {
    super(props);
    this.state = INITIAL_STATE;
    this.isCenterHead = isLoggedInUserCenterHead();
    this.isMultiCenterHead = isLoggedInUserMultiCenterHead();
  }

  extractContentRequestItemIds = () => {
    const { contentRequest } = this.props;
    if (this.isCenterHead || this.isMultiCenterHead) {
      return [];
    }
    const subjects: Subject[] = _.get(
      contentRequest,
      "requestInfo.details.subjects",
      []
    );
    const subjectItemIds = subjects.reduce((acc: string[], subject) => {
      const questions: Question[] = _.get(subject, "questions", []);
      const itemIds = questions.reduce(
        (itemIds: string[], { contentRequestItemsId }) => {
          if (contentRequestItemsId) {
            const id = _.get(
              contentRequestItemsId,
              "_id",
              contentRequestItemsId
            );
            itemIds.push(id);
          }
          return itemIds;
        },
        []
      );

      return acc.concat(itemIds);
    }, []);
    const requestType = _.get(contentRequest, 'requestInfo.type', RequestContentType.QUESTION);
    if(requestType === RequestContentType.TEST) {
      // If request is for test then include contentRequestItemsId from testDetails
      const testContentRequestItemsId = _.get(contentRequest, 'requestInfo.details.testDetails.contentRequestItemsId._id', '');
      if(testContentRequestItemsId) {
        return subjectItemIds.concat(testContentRequestItemsId)
      }
    }
    return subjectItemIds

  };

  contentRequestItemIds = this.extractContentRequestItemIds();

  componentDidMount() {
    this.fetchHistory(1);
  }
  componentDidUpdate(prevProps: Props, prevState: State) {
    if (prevState.currentPage !== this.state.currentPage) {
      this.fetchHistory(this.state.currentPage);
    }
    if (prevProps.contentRequest !== this.props.contentRequest) {
      this.contentRequestItemIds = this.extractContentRequestItemIds();
    }
  }
  groupHistory = (data: RequestHistoryItem[]) => {
    const formattedCreatedAtData = data.map((historyItem) => {
      return {
        ...historyItem,
        createdAt: new Date(historyItem.createdAt),
      };
    });
    // sort by descending createdAt date
    formattedCreatedAtData.sort(
      (a, b) => b.createdAt.getTime() - a.createdAt.getTime()
    );
    const formattedData = formattedCreatedAtData.map((historyItem) => {
      return {
        ...historyItem,
        groupedDate: format(historyItem.createdAt, "dd MMM yyyy"),
      };
    });
    return _.groupBy(formattedData, "groupedDate");
  };
  fetchHistory = async (pageNumber: number) => {
    try {
      const contentRequestId = _.get(
        this.props.contentRequest,
        "_id",
        undefined
      );
      if (!contentRequestId) {
        return;
      }
      // If an author and no contentRequestItemIds exist then don't fetch history(to avoid wrong history)
      if(_.isEmpty(this.contentRequestItemIds) && !this.isCenterHead && !this.isMultiCenterHead) {
        return;
      }
      // Fetch history
      const res = await contentRequestServices.fetchContentRequestHistory({
        pageNumber,
        pageSize: 16,
        contentRequestId,
        contentRequestItemIds:
          this.contentRequestItemIds.join(",") || undefined,
      });
      let { contentRequestHistory, totalNoOfPages } = res;
      this.setState((prevState) => {
        totalNoOfPages = totalNoOfPages || prevState.totalNoOfPages;
        const newHistory = prevState.history.concat(contentRequestHistory);

        return {
          ...prevState,
          history: newHistory,
          totalNoOfPages,
        };
      });
    } catch (error) {
      console.log(error);
    }
  };

  handleViewMore = () => {
    this.setState((prevState) => {
      return {
        ...prevState,
        currentPage: prevState.currentPage + 1,
      };
    });
  };

  getAvatarClass = (userType: string) => {
    const { classes } = this.props;
    switch (userType) {
      case TypeOfAuthor.CREATOR: {
        return classes.creatorAvatar;
      }
      case TypeOfAuthor.EVALUATOR: {
        return classes.evaluatorAvatar;
      }
      case "language": {
        return classes.langEvaluatorAvatar;
      }
      case TypeOfAuthor.MODERATOR: {
        return classes.moderatorAvatar;
      }
      case TypeOfAuthor.PUBLISHER: {
        return classes.publisherAvatar;
      }
      default: {
        return classes.creatorAvatar;
      }
    }
  };
  render() {
    const { open, onClose, classes } = this.props;
    const { history, currentPage, totalNoOfPages } = this.state;

    const groupedHistory = this.groupHistory(history);

    return (
      <Drawer open={open} onClose={onClose} anchor="right">
        <div className={classes.drawerHeader}>
          <Typography variant="title" inline className={classes.drawerHeading}>
            View History
          </Typography>
          <IconButton onClick={onClose} className={classes.closeIcon}>
            <CloseIcon fontSize="inherit" />
          </IconButton>
        </div>
        {_.isEmpty(groupedHistory) && (
          <div className={classes.noHistoryContainer}>
            <img
              src="/assets/svg/contentRequest/no-history.svg"
              alt="no-history"
            />
            <Typography variant="h6">
              There was no history created for this content order.
            </Typography>
          </div>
        )}
        <Stepper orientation="vertical">
          {Object.entries(groupedHistory).map(
            ([groupedDateStr, historyItems]) => {
              return (
                <Step active>
                  <StepLabel
                    icon={
                      <LensIcon className={classes.stepIcon} color="primary" />
                    }
                    classes={{
                      root: classes.stepLabelRoot,
                    }}
                  >
                    <span className={classes.groupedDate}>
                      {groupedDateStr}
                    </span>
                  </StepLabel>
                  <StepContent>
                    {historyItems.map(({ createdAt, message }) => {
                      const date = new Date(createdAt);
                      const userType = _.lowerCase(message.split(" ")[0] || "");
                      return (
                        <Grid container spacing={8} alignItems="center">
                          <Grid item xs="auto">
                            <Avatar
                              className={classNames(
                                classes.avatar,
                                this.getAvatarClass(userType)
                              )}
                            >
                              {message[0]}
                            </Avatar>
                          </Grid>
                          <Grid item xs>
                            <Typography
                              variant="subtitle1"
                              inline
                              className={classes.content}
                            >
                              {message}
                            </Typography>
                          </Grid>
                          <Grid item xs="auto">
                            <Typography
                              variant="subtitle1"
                              inline
                              className={classNames(classes.dateText)}
                            >
                              {format(date, "h:mm a")}
                            </Typography>
                          </Grid>
                        </Grid>
                      );
                    })}
                  </StepContent>
                </Step>
              );
            }
          )}
        </Stepper>
        {currentPage < totalNoOfPages && (
          <div className={classes.spacing}>
            <Button
              variant="text"
              color="primary"
              onClick={this.handleViewMore}
            >
              View older
            </Button>
          </div>
        )}
      </Drawer>
    );
  }
}

// @ts-ignore
export default withStyles(styles)(RequestHistoryDrawer);
